import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { UserData } from '../types/auth';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import GoogleLoginButton from './GoogleLoginButton';
import MicrosoftLoginButton from './MicrosoftLoginButton';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToSignup: () => void;
  onSwitchToForgotPassword?: () => void;
  onLoginSuccess?: (userData: UserData) => void;
}


const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onSwitchToSignup, onSwitchToForgotPassword, onLoginSuccess }) => {
  const { t } = useTranslation();
  
  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);
  
  const {
    // Auth state
    isAuthenticated,
    user,
    
    // Form state
    formData,
    errors,
    showPassword,
    
    // Login state
    isLoggingIn,
    isGoogleLoggingIn,
    isMicrosoftLoggingIn,
    
    // Actions
    setFormData,
    setErrors,
    togglePasswordVisibility,
    handleLogin,
    handleGoogleLogin,
    handleMicrosoftLogin,
    clearErrors,
    clearLoginError,
  } = useAuth();

  // Handle successful login
  useEffect(() => {
    if (isAuthenticated && user && onLoginSuccess) {
      onLoginSuccess(user);
      onClose();
    }
  }, [isAuthenticated, user, onLoginSuccess, onClose]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ [name]: value });
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors({ [name]: undefined });
    }
  };

  // Handle input blur for validation
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newErrors: typeof errors = {};
    
    if (name === 'loginId') {
      if (!value.trim()) {
        newErrors.loginId = t('auth.login.validation.idRequired');
      } else if (value.length < 6) {
        newErrors.loginId = t('auth.login.validation.idMinLength');
      } else if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/.test(value)) {
        newErrors.loginId = t('auth.login.validation.idFormat');
      }
    } else if (name === 'loginPwd') {
      if (!value) {
        newErrors.loginPwd = t('auth.login.validation.passwordRequired');
      } else if (value.length < 6) {
        newErrors.loginPwd = t('auth.login.validation.passwordMinLength');
      } else if (!/^(?=.*[A-Za-z])(?=.*\d)/.test(value)) {
        newErrors.loginPwd = t('auth.login.validation.passwordFormat');
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    }
  };

  // Handle form submission with actual API
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleLogin();
  };

  // Handle modal close
  const handleClose = () => {
    clearErrors();
    clearLoginError();
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="login-dialog-title">
      <div className="bg-dark-800 rounded-lg w-full max-w-md relative my-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          <div className="text-center flex-1">
            <Logo />
            <h2 id="login-dialog-title" className="text-white text-xl mt-1">{t('auth.login.title')}</h2>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close login dialog"
            className="text-white hover:text-gray-300 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* ID Field */}
          <div>
            <label htmlFor="login-id" className="block text-white text-sm font-medium mb-2">{t('auth.login.id')}</label>
            <input
              id="login-id"
              type="text"
              name="loginId"
              value={formData.loginId}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={t('auth.login.placeholders.id')}
              autoComplete="username"
              className={`w-full bg-dark-700 border rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none transition-colors duration-200 ${
                errors.loginId ? 'border-red-500 focus:border-red-400' : 'border-dark-600 focus:border-green-400'
              }`}
            />
            {errors.loginId && (
              <p className="text-red-400 text-xs mt-1">{errors.loginId}</p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="login-password" className="block text-white text-sm font-medium mb-2">{t('auth.login.password')}</label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                name="loginPwd"
                value={formData.loginPwd}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                placeholder={t('auth.login.placeholders.password')}
                autoComplete="current-password"
                className={`w-full bg-dark-700 border rounded-lg px-4 py-3 pr-10 text-white placeholder-gray-400 focus:outline-none transition-colors duration-200 ${
                  errors.loginPwd ? 'border-red-500 focus:border-red-400' : 'border-dark-600 focus:border-green-400'
                }`}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors duration-200"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.loginPwd && (
              <p className="text-red-400 text-xs mt-1">{errors.loginPwd}</p>
            )}
          </div>

          {/* Backend / general error */}
          {errors.general && (
            <div className="bg-red-900/30 border border-red-500 text-red-200 text-sm rounded-lg px-4 py-2">
              {errors.general}
            </div>
          )}

          {/* Forgot Password Link */}
          {onSwitchToForgotPassword && (
            <div className="text-right">
              <button
                type="button"
                onClick={onSwitchToForgotPassword}
                className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
              >
                {t('auth.login.forgotPassword')}
              </button>
            </div>
          )}

          {/* Login Button */}
          <button
            type="submit"
            disabled={isLoggingIn || isGoogleLoggingIn || isMicrosoftLoggingIn}
            className={`w-full text-white font-medium py-3 rounded-lg transition-colors duration-200 ${
              isLoggingIn || isGoogleLoggingIn || isMicrosoftLoggingIn
                ? 'bg-gray-500 cursor-not-allowed' 
                : 'bg-gray-600 hover:bg-gray-700'
            }`}
          >
            {isLoggingIn ? t('common.loading') : t('common.login')}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-dark-600"></div>
            <span className="text-gray-400 text-sm">{t('auth.login.orContinueWith', 'or continue with')}</span>
            <div className="flex-1 h-px bg-dark-600"></div>
          </div>

          {/* Social Login Buttons */}
          <div className="flex flex-col items-center gap-3">
            {/* Google Sign In */}
            <GoogleLoginButton
              onSuccess={(accessToken) => {
                handleGoogleLogin(accessToken);
              }}
              onError={(error) => {
                setErrors({ general: error.message || t('auth.google.error', 'Google sign-in failed') });
              }}
            />
            
            {/* Microsoft Sign In */}
            <MicrosoftLoginButton
              onSuccess={(accessToken) => {
                handleMicrosoftLogin(accessToken);
              }}
              onError={(error) => {
                setErrors({ general: error.message || t('auth.microsoft.error', 'Microsoft sign-in failed') });
              }}
            />
          </div>

          {/* Footer */}
          <div className="text-center">
            <span className="text-gray-300 text-sm">{t('auth.login.noAccount')} </span>
            <button
              type="button"
              onClick={onSwitchToSignup}
              className="text-yellow-400 hover:text-yellow-300 text-sm font-medium transition-colors duration-200"
            >
              {t('auth.login.signUpHere')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body) ?? null;
};

export default LoginModal; 